import ArrowLeft from '@assets/icons/arrow-left.svg';
import DeleteIcon from '@assets/icons/delete.svg';
import { ActivityIndicator } from '@/components/activity-indicator';
import { APP_MODAL_ROUTES } from '@/navigators/routes';
import { AppModalStackParams } from '@/navigators/types';
import { ModalContainer } from '@/components/modal-container';
import { PadAdditionalTerms } from '@/components/payments/pad-additional-terms';
import { PadAgreement } from '@/components/payments/pad-agreement';
import {
  PADConsentFormMarkdown,
  useGetPadConsentFormsForPaymentMethod,
} from '@/hooks/payments/use-pad-payment-operations';
import { StackScreenProps } from '@react-navigation/stack';
import { StyleSheet, View } from 'react-native';
import { useEffect, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type Props = StackScreenProps<
  AppModalStackParams,
  APP_MODAL_ROUTES.ViewPADAgreement
>;

export enum PadAgreementView {
  AdditionalTerms = 'ADDITIONAL_TERMS',
  Agreement = 'AGREEMENT',
}

export const ViewPADAgreementScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const { paymentMethodUUID, restaurantUUID, supplierUUID } = route.params;
  const { bottom } = useSafeAreaInsets();

  const [consentFormsData, setConsentFormsData] =
    useState<PADConsentFormMarkdown | null>(null);
  const [currentView, setCurrentView] = useState(PadAgreementView.Agreement);

  const getPadConsentFormsForPaymentMethod =
    useGetPadConsentFormsForPaymentMethod();

  useEffect(() => {
    const fetchConsentForms = async () => {
      const { data } = await getPadConsentFormsForPaymentMethod({
        variables: {
          paymentMethodUUID,
          restaurantUUID,
          supplierUUID,
        },
      });

      if (
        data?.getPadConsentFormsForPaymentMethod?.pad_agreement &&
        data?.getPadConsentFormsForPaymentMethod
          ?.pad_additional_terms_and_conditions
      ) {
        setConsentFormsData({
          padAdditionalTerms:
            data.getPadConsentFormsForPaymentMethod
              .pad_additional_terms_and_conditions,
          padAgreement: data.getPadConsentFormsForPaymentMethod.pad_agreement,
        });
      }
    };

    fetchConsentForms();
  }, [
    getPadConsentFormsForPaymentMethod,
    paymentMethodUUID,
    restaurantUUID,
    supplierUUID,
  ]);

  const isViewingAgreement = currentView === PadAgreementView.Agreement;

  return (
    <ModalContainer
      navigationHeaderProps={{
        BackButtonIcon: isViewingAgreement ? DeleteIcon : ArrowLeft,
        onBackPress: () =>
          isViewingAgreement
            ? navigation.goBack()
            : setCurrentView(PadAgreementView.Agreement),
        showBackButton: true,
      }}
    >
      <View style={[styles.container, { paddingBottom: bottom }]}>
        {!consentFormsData ? (
          <ActivityIndicator />
        ) : isViewingAgreement ? (
          <PadAgreement
            markdownTemplate={consentFormsData.padAgreement}
            onViewAdditionalTerms={() =>
              setCurrentView(PadAgreementView.AdditionalTerms)
            }
          />
        ) : (
          <PadAdditionalTerms
            markdownTemplate={consentFormsData.padAdditionalTerms}
          />
        )}
      </View>
    </ModalContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 12,
  },
});
